import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  getSessionId,
  getSessionIdFromUrl,
  pathWithSessionId,
  initializeSession,
  cancelCustomerProcessingKeepalive,
} from '../utils/sessionManager';
import { getApiBaseUrl } from '../utils/apiConfig';
import { saveSkinDetection } from '../utils/dataSaver';
import { detectSkinBatch } from '../utils/skinAnalysisApi';
import { fetchAllowedConditionNames } from '../utils/segmentation';
import { getPreviousSkinAnalysis } from '../utils/previousSkinAnalysis';
import {
  normalizeDetections,
  normalizeDescriptionMap,
  normalizeSkinScore,
} from '../utils/skinConditionNormalization';
import {
  REQUEST_STATUS,
  createRequestScope,
  isAbortError,
  isTimeoutError,
  logSalonResilience,
} from '../utils/salonResilience';
import AISkinLoader from '../components/AISkinLoader';
import PageFooter from '../components/PageFooter';
import SalonHeaderActions from '../components/SalonHeaderActions';
import { useSkinSession } from '../context/SkinSessionContext';
import { useSkinAnalysisPlan } from '../context/SkinAnalysisPlanContext';
import './AISkinAnalysisPage.css';
import '../styles/PageBackground.css';
import '../styles/ActionButtons.css';

const CONF_THRESHOLD_MESSAGE = 'Analysis is still running. Please wait a moment…';

function useResolvedSession(location, token) {
  const [sessionId, setSessionId] = useState(() => {
    const fromState = location.state?.sessionId;
    const fromUrl = getSessionIdFromUrl(location);
    const fromStorage = getSessionId();
    return fromState ?? fromUrl ?? fromStorage ?? null;
  });
  const apiBaseUrl = getApiBaseUrl();

  useEffect(() => {
    const fromState = location.state?.sessionId;
    const fromUrl = getSessionIdFromUrl(location);
    const fromStorage = getSessionId();
    const resolved = fromState || fromUrl || fromStorage;

    if (resolved && sessionId !== resolved) {
      setSessionId(resolved);
      return;
    }
    if (sessionId) return;

    const init = async () => {
      const sid = await initializeSession(apiBaseUrl, token ? { app_session_token: token } : {});
      setSessionId(sid);
    };
    init();
  }, [apiBaseUrl, sessionId, location, token]);

  return [sessionId, setSessionId];
}

const AISkinAnalysisPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, sessionLandingPath } = useSkinSession();
  const {
    ENABLE_OLLAMA_DESCRIPTIONS,
    ENABLE_RADAR_CHART,
    ENABLE_BEFORE_RADAR_CHART,
  } = useSkinAnalysisPlan();

  const capturedImage = location.state?.capturedImage ?? null;
  const sideCaptures = location.state?.sideCaptures ?? null;
  const userDataFromState = location.state?.userData ?? null;
  const aqiDataFromState = location.state?.aqiData ?? null;

  const [sessionId, setSessionId] = useResolvedSession(location, token);
  const [isDetecting, setIsDetecting] = useState(false);
  const [error, setError] = useState(null);
  const [requestStatus, setRequestStatus] = useState(REQUEST_STATUS.IDLE);
  const [detectProgress, setDetectProgress] = useState(null);

  const hasDetectedRef = useRef(false);
  const detectionInProgressRef = useRef(false);
  const requestScopeRef = useRef(null);
  const retryCountRef = useRef(0);
  const manualRetryBusyRef = useRef(false);
  const sessionIdRef = useRef(sessionId);
  const activeDetectRequestIdRef = useRef(null);
  const isDetectingRef = useRef(isDetecting);
  sessionIdRef.current = sessionId;
  isDetectingRef.current = isDetecting;

  const cancelServerProcessingIfNeeded = useCallback(
    (reason, ownerRequestId) => {
      if (reason === 'retry-replacement') return;
      if (!detectionInProgressRef.current && !isDetectingRef.current) return;
      const owner =
        ownerRequestId
        || activeDetectRequestIdRef.current;
      if (!owner) return;
      const sid =
        sessionIdRef.current
        ?? getSessionIdFromUrl(location)
        ?? getSessionId();
      if (!sid) return;
      cancelCustomerProcessingKeepalive(getApiBaseUrl(), sid, owner, token || null);
      logSalonResilience('processing_cancel_keepalive', { reason, sessionId: sid, ownerRequestId: owner });
    },
    [location, token]
  );

  const abortActiveRequest = useCallback((reason = 'navigation') => {
    const scope = requestScopeRef.current;
    if (scope) {
      logSalonResilience('abort', { label: 'detect-skin-page', reason, requestId: scope.requestId });
      scope.abort(reason);
      requestScopeRef.current = null;
    }
    if (reason !== 'retry-replacement') {
      hasDetectedRef.current = false;
      detectionInProgressRef.current = false;
    }
    cancelServerProcessingIfNeeded(reason, scope?.requestId);
  }, [cancelServerProcessingIfNeeded]);

  const abortActiveRequestRef = useRef(abortActiveRequest);
  abortActiveRequestRef.current = abortActiveRequest;

  useEffect(() => () => abortActiveRequestRef.current('unmount'), []);

  useEffect(() => {
    const onPageHide = () => {
      if (detectionInProgressRef.current || isDetectingRef.current) {
        cancelServerProcessingIfNeeded('pagehide', activeDetectRequestIdRef.current);
      }
    };
    window.addEventListener('pagehide', onPageHide);
    return () => window.removeEventListener('pagehide', onPageHide);
  }, [cancelServerProcessingIfNeeded]);

  const navigateAfterAnalysis = useCallback(
    (skinResults, finalSessionId, aiConditionDescriptions = null, previousSkinData = null) => {
      navigate(pathWithSessionId('/skin-routine', finalSessionId), {
        state: {
          capturedImage,
          sideCaptures: sideCaptures || undefined,
          userData: userDataFromState,
          sessionId: finalSessionId,
          skinResults,
          aqiData: aqiDataFromState,
          aiConditionDescriptions: aiConditionDescriptions ?? null,
          previousSkinData: previousSkinData ?? null,
          skinGoalData: { goals: [] },
          ...(token && { token }),
        },
        replace: true,
      });
    },
    [navigate, capturedImage, sideCaptures, userDataFromState, aqiDataFromState, token]
  );

  const runDetection = useCallback(
    async (imageDataUrl, { isManualRetry = false } = {}) => {
      if (
        !imageDataUrl ||
        !sessionId ||
        hasDetectedRef.current ||
        detectionInProgressRef.current ||
        (isManualRetry && manualRetryBusyRef.current)
      ) {
        return;
      }

      if (isManualRetry) manualRetryBusyRef.current = true;
      abortActiveRequest('retry-replacement');
      const scope = createRequestScope();
      requestScopeRef.current = scope;
      activeDetectRequestIdRef.current = scope.requestId;

      hasDetectedRef.current = true;
      detectionInProgressRef.current = true;
      setIsDetecting(true);
      setError(null);
      setRequestStatus(REQUEST_STATUS.PROCESSING);
      setDetectProgress({ phase: 'starting', message: 'Preparing analysis…', batchSize: 1 });

      const currentSessionId = sessionId ?? location.state?.sessionId ?? getSessionId();
      const userAge = userDataFromState?.age ?? null;

      const finishWithError = (message, status = REQUEST_STATUS.RETRY_AVAILABLE) => {
        hasDetectedRef.current = false;
        detectionInProgressRef.current = false;
        setError(message);
        setRequestStatus(status);
        setIsDetecting(false);
      };

      try {
        const [allowedConditions, previousRes] = await Promise.all([
          token
            ? fetchAllowedConditionNames(token).then((allowedSet) => (
              allowedSet ? Array.from(allowedSet) : null
            ))
            : Promise.resolve(null),
          ENABLE_RADAR_CHART && ENABLE_BEFORE_RADAR_CHART
            ? getPreviousSkinAnalysis(currentSessionId, token)
            : Promise.resolve({ success: true, previous: null }),
        ]);

        if (scope.isAborted()) return;

        const data = await detectSkinBatch({
          frontImage: imageDataUrl,
          sideCaptures: sideCaptures || undefined,
          sessionId: currentSessionId,
          age: userAge,
          allowedConditions,
          appSessionToken: token || null,
          useOllamaForDescriptions: Boolean(ENABLE_OLLAMA_DESCRIPTIONS),
          retryCount: retryCountRef.current,
          signal: scope.signal,
          requestId: scope.requestId,
          onProgress: setDetectProgress,
          onRetry: ({ reconnecting, timeout }) => {
            setRequestStatus(
              reconnecting || timeout ? REQUEST_STATUS.RECONNECTING : REQUEST_STATUS.RETRYING
            );
            setDetectProgress((prev) => ({
              ...(prev || {}),
              phase: 'retry',
              message: reconnecting
                ? 'Reconnecting to server…'
                : CONF_THRESHOLD_MESSAGE,
            }));
          },
        });

        if (scope.isAborted()) return;

        if (!data.success || !data.marked_image) {
          const persistFail = data?.persistence_failed;
          finishWithError(
            data.error || (persistFail ? 'Failed to save analysis results. Please try again.' : 'Detection failed'),
            persistFail
              ? (data?.retryable === false ? REQUEST_STATUS.FAILED : REQUEST_STATUS.RETRY_AVAILABLE)
              : REQUEST_STATUS.FAILED
          );
          return;
        }

        if (data.persistence_verified !== true) {
          finishWithError(
            'Analysis completed but persistence was not verified. Please try again.',
            REQUEST_STATUS.RETRY_AVAILABLE
          );
          return;
        }

        const detectionsList = normalizeDetections(data.detections ?? []);
        const frontDetections = normalizeDetections(data.front_detections ?? data.detections ?? []);
        const finalSkinConditions = data.skin_conditions ?? null;
        const finalSkinScore = normalizeSkinScore(data.skin_score ?? null);
        const hadSessionFromState = !!(sessionId ?? location.state?.sessionId);
        const finalSessionId = hadSessionFromState
          ? currentSessionId
          : (data.session_id || currentSessionId);

        if (data.session_id && !hadSessionFromState) {
          setSessionId(data.session_id);
        }

        setDetectProgress({ phase: 'persist', message: 'Saving your results…', batchSize: 1 });

        const saveResult = await saveSkinDetection(
          finalSessionId,
          {
            detections: detectionsList,
            frontDetections,
            sideResults: data.side_results || null,
            skinScore: finalSkinScore,
            skinConditions: finalSkinConditions,
            markedImage: data.marked_image,
          },
          userDataFromState ?? {},
          token ?? null
        );

        if (scope.isAborted()) return;

        if (!saveResult?.success) {
          finishWithError(
            saveResult?.error || 'Failed to save analysis. Please try again.',
            saveResult?.retryable === false ? REQUEST_STATUS.FAILED : REQUEST_STATUS.RETRY_AVAILABLE
          );
          return;
        }

        const aiDescriptions =
          ENABLE_OLLAMA_DESCRIPTIONS
          && data?.ai_condition_descriptions
          && typeof data.ai_condition_descriptions === 'object'
            ? normalizeDescriptionMap(data.ai_condition_descriptions)
            : {};

        const previousSkinData =
          previousRes.success && previousRes.previous ? previousRes.previous : null;

        navigateAfterAnalysis(
          {
            detections: detectionsList,
            frontDetections,
            skinScore: finalSkinScore,
            skinConditions: finalSkinConditions,
            markedImage: data.marked_image,
            faceShapes: data.face_shapes || null,
            sideResults: data.side_results || null,
            aiConditionDescriptionsPending: Boolean(data.ai_condition_descriptions_pending),
          },
          finalSessionId,
          aiDescriptions,
          previousSkinData
        );
      } catch (err) {
        if (isAbortError(err)) return;

        if (err.status === 409) {
          finishWithError(err.apiResponse?.error || CONF_THRESHOLD_MESSAGE, REQUEST_STATUS.QUEUED);
          return;
        }
        if (isTimeoutError(err)) {
          finishWithError(
            'Analysis timed out. The server may still be processing — try again in a moment.',
            REQUEST_STATUS.TIMEOUT
          );
          return;
        }
        if (err.status === 503 || err.retryable) {
          finishWithError(
            err.apiResponse?.error || err.message || 'Processing interrupted. Please try again.',
            REQUEST_STATUS.RETRY_AVAILABLE
          );
          return;
        }
        finishWithError(err.message || 'Failed to detect skin conditions', REQUEST_STATUS.FAILED);
      } finally {
        if (requestScopeRef.current === scope) {
          requestScopeRef.current = null;
        }
        if (activeDetectRequestIdRef.current === scope.requestId) {
          activeDetectRequestIdRef.current = null;
        }
        detectionInProgressRef.current = false;
        setIsDetecting(false);
        manualRetryBusyRef.current = false;
      }
    },
    [
      sessionId,
      setSessionId,
      location.state?.sessionId,
      userDataFromState,
      token,
      sideCaptures,
      ENABLE_OLLAMA_DESCRIPTIONS,
      ENABLE_RADAR_CHART,
      ENABLE_BEFORE_RADAR_CHART,
      navigateAfterAnalysis,
      abortActiveRequest,
    ]
  );

  const runDetectionRef = useRef(runDetection);
  runDetectionRef.current = runDetection;

  useEffect(() => {
    if (
      capturedImage &&
      sessionId &&
      !hasDetectedRef.current &&
      !detectionInProgressRef.current
    ) {
      runDetectionRef.current(capturedImage);
    }
  }, [capturedImage, sessionId]);

  const handleBack = useCallback(() => {
    abortActiveRequest('route-change');
    const sid = sessionId ?? getSessionIdFromUrl(location) ?? getSessionId();
    navigate(pathWithSessionId('/capture', sid), {
      state: {
        capturedImage,
        userData: userDataFromState,
        sessionId,
        aqiData: aqiDataFromState,
        ...(token && { token }),
      },
    });
  }, [navigate, sessionId, location, capturedImage, userDataFromState, aqiDataFromState, token, abortActiveRequest]);

  const handleHome = useCallback(() => {
    abortActiveRequest('route-change');
    navigate(sessionLandingPath);
  }, [navigate, sessionLandingPath, abortActiveRequest]);

  const handleRetry = useCallback(() => {
    if (manualRetryBusyRef.current || detectionInProgressRef.current) return;
    retryCountRef.current += 1;
    hasDetectedRef.current = false;
    runDetection(capturedImage, { isManualRetry: true });
  }, [runDetection, capturedImage]);

  const loaderMessage = detectProgress?.message || 'Analyzing your skin…';
  const showLoader = !error && (isDetecting || requestStatus === REQUEST_STATUS.PROCESSING);

  if (!capturedImage) {
    return (
      <div className="ny-salon-analysis page-bg">
        <header className="ny-salon-analysis__nav" role="banner">
          <button
            type="button"
            className="ny-salon-analysis__back"
            onClick={handleBack}
            aria-label="Go back"
          >
            ←
          </button>
          <SalonHeaderActions homeButtonClassName="ny-salon-analysis__home" onHome={handleHome} />
        </header>
        <main className="ny-salon-analysis__main">
          <div className="ny-salon-analysis__error">
            <h2>No Image Found</h2>
            <p>Please capture an image first.</p>
            <button type="button" className="skin-action-button" onClick={handleBack}>
              Go Back
            </button>
          </div>
        </main>
        <PageFooter />
      </div>
    );
  }

  return (
    <div className="ny-salon-analysis page-bg">
      <header className="ny-salon-analysis__nav" role="banner">
        <button
          type="button"
          className="ny-salon-analysis__back"
          onClick={handleBack}
          aria-label="Go back"
        >
          ←
        </button>
        <div className="ny-salon-analysis__title-wrap">
          <SalonHeaderActions homeButtonClassName="ny-salon-analysis__home" onHome={handleHome} />
        </div>
      </header>
      <main className="ny-salon-analysis__main">
        {error ? (
          <div className="ny-salon-analysis__error">
            <h2>
              {requestStatus === REQUEST_STATUS.TIMEOUT
                ? 'Timed Out'
                : requestStatus === REQUEST_STATUS.QUEUED
                  ? 'Please Wait'
                  : 'Error'}
            </h2>
            <p>{error}</p>
            <button type="button" className="skin-action-button" onClick={handleRetry}>
              Try Again
            </button>
          </div>
        ) : showLoader ? (
          <AISkinLoader
            imageSrc={capturedImage}
            progressMessage={loaderMessage}
            requestStatus={requestStatus}
          />
        ) : null}
      </main>
      <PageFooter />
    </div>
  );
};

export default AISkinAnalysisPage;
